import { useState, useEffect } from "react";
import { API, useAdmin } from "../App";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from "recharts";
import { Eye, Users, Calendar, Clock, TrendingUp, BookOpen } from "lucide-react";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function ViewersTab() {
  const { adminToken } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/viewer-analytics`, {
          headers: { Authorization: `Bearer ${adminToken}` },
          withCredentials: true
        });
        setData(res.data);
      } catch (e) {
        console.error("Failed to fetch viewer analytics:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [adminToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#C2B096', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-gray-400 py-12">Unable to load viewer analytics.</p>;
  }

  const { summary, daily, day_of_week, top_books } = data;

  return (
    <div className="space-y-6" data-testid="viewers-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { label: "Today", views: summary.today_views, unique: summary.today_unique, icon: Eye, color: "bg-[#795A3B]" },
          { label: "This Week", views: summary.week_views, unique: summary.week_unique, icon: Calendar, color: "bg-[#5D4228]" },
          { label: "This Month", views: summary.month_views, unique: summary.month_unique, icon: TrendingUp, color: "bg-[#8A7560]" },
          { label: "All Time", views: summary.total_views, unique: summary.total_unique, icon: Users, color: "bg-[#472D1E]" },
          { label: "Avg Views/Visitor", views: summary.avg_views_per_visitor, unique: null, icon: Clock, color: "bg-[#A99680]" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={`viewer-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{s.views}</p>
            <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
            {s.unique !== null && <p className="text-gray-400 text-[10px] mt-0.5">{s.unique} unique visitors</p>}
          </div>
        ))}
      </div>

      {/* Daily Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="viewers-daily-chart">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Daily Page Views (Last 30 Days)</h3>
        {daily.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={daily}>
              <defs>
                <linearGradient id="vViewsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#795A3B" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#795A3B" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="vUniqueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D1A86E" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#D1A86E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                tickFormatter={(d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="views" name="Page Views" stroke="#795A3B" strokeWidth={2} fill="url(#vViewsGrad)" />
              <Area type="monotone" dataKey="unique" name="Unique Visitors" stroke="#D1A86E" strokeWidth={2} fill="url(#vUniqueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-gray-400 text-center py-16">No visitor data yet.</p>
        )}
        <div className="flex items-center gap-6 mt-2 justify-center">
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <span className="w-3 h-1 rounded" style={{ background: '#795A3B' }} /> Page Views
          </span>
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <span className="w-3 h-1 rounded" style={{ background: '#D1A86E' }} /> Unique Visitors
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day of Week Chart */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="viewers-dow-chart">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Views by Day of Week</h3>
          {day_of_week.some(d => d.views > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={day_of_week}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(0, 3)} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="views" name="Total Views" fill="#795A3B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-sm text-center py-12">No data yet.</p>
          )}
        </div>

        {/* Most Viewed Books */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="viewers-top-books">
          <h3 className="text-base font-semibold text-gray-800 mb-4">
            <BookOpen className="w-4 h-4 inline mr-2" style={{ color: '#795A3B' }} />
            Most Viewed Books
          </h3>
          {top_books.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {top_books.map((book, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white`} style={{ background: i < 3 ? '#795A3B' : '#A99680' }}>
                    {i + 1}
                  </span>
                  <div className="w-8 h-11 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {book.cover_image && (
                      <img src={book.cover_image} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{book.title}</p>
                    <p className="text-xs text-gray-500">{book.author}</p>
                  </div>
                  <span className="flex items-center gap-1 text-sm font-semibold text-gray-600">
                    <Eye className="w-3.5 h-3.5 text-gray-400" />
                    {book.views}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <Eye className="w-10 h-10 mx-auto text-gray-200 mb-2" />
              <p className="text-gray-400 text-sm">Book view tracking is active.</p>
              <p className="text-gray-300 text-xs mt-1">Data appears as visitors browse books.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
